import { ReactNode } from 'react';
import { array } from 'fp-ts';
import { Dash, DividerList, LabeledListItem, ContentSection } from '@mews-commander/ui';
import { LocalizedCurrencyValue, LocalizedPercentage, lt, NewLocalizedNumber } from '@mews/framework';
import { UnicodeEntity } from '@mews/enums';
import { BookablePeriod, PortfolioBookablePeriod } from '@mews-commander/data-access';
import {
    CancellationPolicyTiming,
    FeeExtent,
    LocalizedCancellationPolicyBookablePeriod,
    LocalizedSelectedBookablePeriod,
} from '../../../components';

interface Props {
    rightContent?: ReactNode;
    name?: string | null;
    bookablePeriod?: BookablePeriod | PortfolioBookablePeriod | null;
    policyKey?: string | null;
    absoluteFee?: number;
    absoluteFeeCurrencyCode?: string;
    relativeFee: number;
    applicabilityType: 'Creation' | 'Start' | 'StartDate';
    applicabilityOffset: string;
    feeExtent?: Array<'TimeUnits' | 'Products'> | null;
    feeMaximumTimeUnits?: number | null;
}

export const CancellationPolicyDetail = ({
    rightContent,
    name,
    bookablePeriod,
    policyKey,
    absoluteFee,
    absoluteFeeCurrencyCode,
    relativeFee,
    applicabilityType,
    applicabilityOffset,
    feeExtent,
    feeMaximumTimeUnits,
}: Props) => (
    <ContentSection title={lt('DetailPlural')} rightContent={rightContent}>
        <DividerList>
            <LabeledListItem label={lt('Name')} contentOptions={{ wordBreak: 'break-all' }}>
                {name || <Dash />}
            </LabeledListItem>
            {bookablePeriod ? (
                <LabeledListItem label={lt('BookablePeriod')}>
                    <LocalizedCancellationPolicyBookablePeriod bookablePeriod={bookablePeriod} />
                </LabeledListItem>
            ) : null}
            <LabeledListItem label={lt('CancellationPolicyFormCancellationPolicyKeyFieldName')}>
                {policyKey ?? UnicodeEntity.Dash}
            </LabeledListItem>
            {absoluteFee ? (
                <LabeledListItem label={lt('GeneralFixedAmount')}>
                    {absoluteFeeCurrencyCode ? (
                        <LocalizedCurrencyValue value={{ value: absoluteFee, currency: absoluteFeeCurrencyCode }} />
                    ) : (
                        <NewLocalizedNumber value={absoluteFee} />
                    )}
                </LabeledListItem>
            ) : null}
            <LabeledListItem label={lt('GeneralPercentage')}>
                <LocalizedPercentage value={relativeFee} options={{ maximumFractionDigits: 2 }} />
            </LabeledListItem>
            <LabeledListItem label={lt('Deadline')}>
                <CancellationPolicyTiming
                    applicabilityType={applicabilityType}
                    applicabilityOffset={applicabilityOffset}
                />
            </LabeledListItem>
            {feeExtent && array.isNonEmpty(feeExtent) ? (
                <LabeledListItem label={lt('FeeExtent')}>
                    <FeeExtent bookablePeriod={bookablePeriod ?? BookablePeriod.Night} feeExtent={feeExtent} />
                </LabeledListItem>
            ) : null}
            {bookablePeriod ? (
                <LabeledListItem label={lt('CancellationPolicyMaxPeriodsToApplyFee')}>
                    {feeMaximumTimeUnits ? (
                        <LocalizedSelectedBookablePeriod
                            bookablePeriod={bookablePeriod}
                            substitutes={{ count: feeMaximumTimeUnits }}
                        />
                    ) : (
                        UnicodeEntity.Dash
                    )}
                </LabeledListItem>
            ) : null}
        </DividerList>
    </ContentSection>
);
